import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sidebar } from './Sidebar';
import { FileExplorer } from './FileExplorer';
import { BreadcrumbNavigation } from './BreadcrumbNavigation';
import { StorageInfo } from './StorageInfo';
import { FileDetails } from './FileDetails';
import { CreateFolderModal } from './CreateFolderModal';
import { Toast } from './Toast';
import {
  createFolder,
  uploadFile,
  deleteFolder,
  deleteSingleFile,
} from '../../services/assetManagerService';
import { Asset } from './FileExplorer';
import { useSearchParams } from 'react-router-dom';
import { usePageContext } from '../../contexts/PageContext';
import FileUploadModal from './FileUploadModal';
import { Folder, ChevronRight } from 'lucide-react';

interface AssetManagerProps {
  isPicker?: boolean;
  allowedFileTypes?: string[];
  multiSelect?: boolean;
  onSelect?: (selectedAssets: Asset[]) => void;
}

const AssetManager: React.FC<AssetManagerProps> = ({
  isPicker = false,
  allowedFileTypes,
  multiSelect = true,
  onSelect,
}) => {
  const fileExplorerRef = useRef<{ clearSearch: () => void }>(null);
  const { collapsed } = usePageContext();
  const [searchParams] = useSearchParams();

  const initialFolder = isPicker ? '/' : searchParams.get('folder') || '/';
  const [currentFolder, setCurrentFolder] = useState<string>(initialFolder);

  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] =
    useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [directoryUpdateTrigger, setDirectoryUpdateTrigger] = useState(0);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [fileListUpdates, setFileListUpdates] = useState(1);
  const [createDisable, setCreateDisable] = useState(false)

  const handleNavigate = useCallback((folder: string) => {
    if (folder === currentFolder) return;
    setCurrentFolder(folder);
    fileExplorerRef.current?.clearSearch();
    if (!isPicker) {
      window.history.pushState({ folder }, '', `?folder=${folder}`);
    }
  }, [currentFolder, isPicker]);
  const [showfolder, setshowfolder] = useState(false);
  const selectedFolder = () => {
    setshowfolder((prevState) => !prevState);
  };

  // Update navigation state when directory updates
  useEffect(() => {
    if (isNavigating) {
      setIsNavigating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directoryUpdateTrigger]);

  // Reset navigation state if it gets stuck
  useEffect(() => {
    if (isNavigating) {
      const timer = setTimeout(() => {
        setIsNavigating(false);
      }, 3000); // Failsafe timeout
      return () => clearTimeout(timer);
    }
  }, [isNavigating]);

  useEffect(() => {
    if (isPicker) return; // Don't handle history for picker mode

    const handlePopState = (event: PopStateEvent) => {
      const folder = event.state?.folder || '/';
      setCurrentFolder(folder);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isPicker]);

  useEffect(() => {
    if (!isPicker) {
      const folder = searchParams.get('folder') || '/';
      setCurrentFolder(folder);
      // Set initial history state if not already set
      if (!window.history.state?.folder) {
        window.history.replaceState({ folder }, '', `?folder=${folder}`);
      }
    }
  }, [isPicker, searchParams]);

  const handleCreateFolder = async (folderName: string) => {
    try {
      setCreateDisable(true)
      await createFolder(`${currentFolder}${folderName}`);
      setToast({ message: 'Folder created successfully', type: 'success' });
      setDirectoryUpdateTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('Error creating folder:', error);
      setToast({ message: 'Failed to create folder', type: 'error' });
    }
    setCreateDisable(false)
    setIsCreateFolderModalOpen(false);
  };

  const handleDeleteFolder = async (folderPath: string) => {
    try {
      await deleteFolder(folderPath);
      setToast({ message: 'Folder deleted successfully', type: 'success' });
      setDirectoryUpdateTrigger((prev) => prev + 1);

      if (folderPath === currentFolder) {
        setCurrentFolder('/');
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      setToast({ message: 'Failed to delete folder', type: 'error' });
    }
  };
  const fileChangesInSize = () => {
    setFileListUpdates((prev) => prev + 1);
  }

  const handleFileUpload = async (
    files: File[],
    onProgress: (file: File, progress: number) => void
  ) => {
    setIsUploading(true);
    try {
      for (const file of files) {
        await uploadFile(file, currentFolder, (progress) =>
          onProgress(file, progress)
        );
      }
      setToast({ message: 'Files uploaded successfully', type: 'success' });
      setIsUploadModalOpen(false);
      setDirectoryUpdateTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('Error uploading file:', error);
      setToast({ message: 'Failed to upload file', type: 'error' });
    }
    setIsUploading(false);
  };

  const handleDelete = async (asset: Asset) => {
    try {
      await deleteSingleFile(asset.id || '');
      setToast({ message: 'File deleted successfully', type: 'success' });
      setDirectoryUpdateTrigger((prev) => prev + 1);
      setSelectedAsset(null);
    } catch (error) {
      console.error('Error deleting file:', error);
      setToast({ message: 'Failed to delete file', type: 'error' });
    }
  };

  const handleConfirmSelection = () => {
    if (onSelect) {
      onSelect(selectedAssets);
    }
  };

  const sideBar = () => {
    return (
      <>
      <button className="sm:hidden fixed top-20 bg-green-200 rounded-lg p-4 font-medium left-4 right-4 text-left flex items-center justify-between" onClick={selectedFolder}>
        <span className='flex items-center gap-2'><Folder /> Image Folders List</span> <ChevronRight />
      </button>
      <div className={`w-auto sm:w-64 border-gray-200 bg-green-200 sm:bg-white 
          ${isPicker ? 'border-r' : 'fixed top-[124px] sm:top-0 sm:bottom-0 border-0 sm:border-x rounded-bl-lg rounded-br-lg sm:rounded-none sm: max-h-96 sm:max-h-full overflow-auto'} 
          ${!isPicker && (collapsed ? 'left-4 right-4 sm:right-auto sm:left-16' : 'left-64')}
          transition-all 
          duration-200
          ${showfolder ? "visible" : "sm:block hidden"}
        `}
      >
        <Sidebar
          onDeleteFolder={isPicker ? undefined : handleDeleteFolder}
          onNavigate={handleNavigate}
          currentFolder={currentFolder}
          directoryUpdateTrigger={directoryUpdateTrigger}
          isPicker={isPicker}
        />
      </div>
      </>
    );
  };

  return (
    <div className="flex h-full">
      {isNavigating && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="h-1 bg-orange-100">
            <div className="h-full w-1/3 bg-orange-600 animate-slide" />
          </div>
        </div>
      )}

      {isPicker && sideBar()}

      <div className={`
        flex-1 
        transition-all 
        duration-200 ${isPicker ? 'p-10 bg-primary-50 relative' : 'w-full pl-0 sm:pl-64'} 
      `}>
          <BreadcrumbNavigation
            currentFolder={currentFolder}
            onNavigate={handleNavigate}
          />
          {!isPicker && <StorageInfo key={fileListUpdates} />}
          <FileExplorer
            ref={fileExplorerRef}
            currentFolder={currentFolder}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onCreateFolder={() => setIsCreateFolderModalOpen(true)}
            selectedAssets={selectedAssets}
            setSelectedAssets={setSelectedAssets}
            allowedFileTypes={allowedFileTypes}
            multiSelect={multiSelect}
            isPicker={isPicker}
            onNavigate={handleNavigate}
            setIsUploadModalOpen={setIsUploadModalOpen}
            setIsNavigating={setIsNavigating}
            fileChangesInSize={fileChangesInSize}
          />

      {isPicker && selectedAssets.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 border-t p-4 flex justify-end z-20">
          <button
            onClick={handleConfirmSelection}
            className="bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 transition duration-200"
          >
            Continue
          </button>
        </div>
      )}
      </div>

      <FileUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleFileUpload}
        isUploading={isUploading}
      />

      {!isPicker && selectedAsset && (
        <FileDetails
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onDelete={handleDelete}
          isPicker={isPicker}
        />
      )}

      {!isPicker && (
        <CreateFolderModal
          isOpen={isCreateFolderModalOpen}
          onClose={() => setIsCreateFolderModalOpen(false)}
          onCreateFolder={handleCreateFolder}
          setDisable={createDisable}
        />
      )}

      <Toast
        message={toast?.message || ''}
        type={toast?.type || 'success'}
        onClose={() => setToast(null)}
      />
      {!isPicker && sideBar()}
    </div>
  );
};

export default AssetManager;
